import { useDisclosure } from "@mantine/hooks";

import * as Sentry from "@sentry/react";

import { toast } from "@/utils/notifications";

export type DestructiveConfirmActionResult =
  | { ok: true }
  | { ok: false; errorToast?: DestructiveConfirmToast };

interface DestructiveConfirmToast {
  title?: string;
  message: string;
  autoClose?: boolean | number;
}

export interface UseDestructiveConfirmActionOptions {
  opened: boolean;
  onClose: () => void;
  onRefresh: () => void | Promise<void>;
  action: () => Promise<DestructiveConfirmActionResult>;
  errorToast: DestructiveConfirmToast;
  successToast?: DestructiveConfirmToast;
}

/**
 * Orchestrates destructive confirmation flows: loading state, action execution,
 * refresh, close, and success/error toasts.
 */
export function useDestructiveConfirmAction({
  opened,
  onClose,
  onRefresh,
  action,
  errorToast,
  successToast,
}: UseDestructiveConfirmActionOptions) {
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(false);

  const handleConfirm = async () => {
    if (!opened) return;

    try {
      startLoading();
      const result = await action();

      if (!result.ok) {
        const failureToast = result.errorToast ?? errorToast;
        toast({
          title: failureToast.title ?? "Error",
          message: failureToast.message,
          type: "error",
          autoClose: failureToast.autoClose ?? 5000,
        });
        return;
      }

      await onRefresh();
      onClose();

      if (successToast) {
        toast({
          title: successToast.title,
          message: successToast.message,
          type: "success",
          autoClose: successToast.autoClose ?? 3000,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      toast({
        title: errorToast.title ?? "Error",
        message: errorToast.message,
        type: "error",
        autoClose: errorToast.autoClose ?? 5000,
      });
    } finally {
      stopLoading();
    }
  };

  return { isLoading, handleConfirm };
}
